import React, { useEffect, useState } from "react";

import { NavigationButtonTranslations } from "@/components/organisms/FooterFlowButtons/FooterFlowButtons";

import { WizardContext, WizardStep } from "../types";
import Wizard, { WizardProps } from "../Wizard";
import { StyledWizardSubtitle } from "./styled";
import WizardFormProvider from "./WizardFormProvider";

export interface WizardFormProps<FormType>
    extends Omit<
        WizardProps,
        "wizardStep" | "onStep" | "triggerForm" | "wizardSteps" | "onFinish" | "navigationLabels" | "canContinue"
    > {
    defaultFormValues: FormType;
    wizardSteps: WizardStep[];
    defaultStepId?: number;
    title?: string;
    blockNavigationFormInvalid?: boolean;
    handleSubmit: (values: FormType) => void;
    onChangeWizardStep?: (wizardStep: WizardStep, values: FormType) => void;
    getNavigationLabels?: ({ values, step }: { values: FormType; step: WizardStep }) => NavigationButtonTranslations;
    shouldHideStepBar?: (values: FormType) => boolean;
    prepareDataForValidation?: (values: FormType) => FormType;
}

const WizardForm = <FormType,>({
    wizardSteps,
    defaultStepId = 0,
    defaultFormValues,
    handleSubmit,
    onChangeWizardStep,
    getNavigationLabels,
    shouldHideStepBar,
    title,
    blockNavigationFormInvalid = false,
    prepareDataForValidation,
    ...wizardProps
}: WizardFormProps<FormType>): React.ReactElement => {
    const [wizardStep, setWizardStep] = useState<WizardStep>(wizardSteps[defaultStepId]);

    const wizardContext: WizardContext = { wizardStep };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [wizardStep]);

    return (
        <>
            {!!title && <StyledWizardSubtitle>{title}</StyledWizardSubtitle>}

            <WizardFormProvider
                formContext={wizardContext}
                formDefaultValues={defaultFormValues}
                prepareDataForValidation={prepareDataForValidation}
            >
                {(formMethods) => {
                    const values = formMethods.watch();
                    const wizardStepsFiltered = wizardSteps
                        .filter((step) => (step?.isEnabled ? step.isEnabled(values) : true))
                        .map((step) => ({ ...step, title: step.dynamicTitle?.(values) || step.title }));

                    const navigationLabels = getNavigationLabels
                        ? getNavigationLabels({ values, step: wizardStep })
                        : null;

                    const handleChangeWizardStep = (step: WizardStep) => {
                        onChangeWizardStep?.(step, values);
                        setWizardStep(step);
                    };

                    const hideStepBar = shouldHideStepBar ? shouldHideStepBar(values) : false;

                    return (
                        <Wizard
                            wizardStep={wizardStep}
                            onStep={handleChangeWizardStep}
                            canContinue={formMethods.trigger}
                            wizardSteps={wizardStepsFiltered}
                            navigationLabels={navigationLabels}
                            blockNavigation={blockNavigationFormInvalid && !formMethods.formState.isValid}
                            hideStepBar={hideStepBar}
                            onFinish={formMethods.handleSubmit(handleSubmit)}
                            {...wizardProps}
                        />
                    );
                }}
            </WizardFormProvider>
        </>
    );
};
export default WizardForm;
